import type { FC, ReactNode } from 'react'
import type { Quote } from '@/types'

import Image from 'next/image'
import { useEffect, useState } from 'react'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { Quote as QuoteIcon } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useSiteContent } from '@/lib/site-content'
import CmsText from '@/components/generals/cms-text'
import { FadeIn } from '@/components/motion/fade-in'
import FloatingBlob from '@/components/motion/floating-blob'

// Organic blob silhouette, the same border-radius the legacy design used.
const BLOB_RADIUS = '42% 35% 36% 41% / 39% 40% 68% 54%'

interface QuotesProps {
    quotes: Quote[]
}

const Quotes: FC<QuotesProps> = ({ quotes }): ReactNode => {
    const { settings } = useSiteContent()
    const [index, setIndex] = useState(0)
    const [paused, setPaused] = useState(false)
    const reduce = useReducedMotion()

    const seconds = settings.quotes_rotate_seconds && settings.quotes_rotate_seconds > 0 ? settings.quotes_rotate_seconds : 8
    const rotateMs = seconds * 1000
    const count = quotes.length

    useEffect(() => {
        if (paused || reduce || count <= 1) return

        const id = window.setInterval(() => {
            setIndex(i => (i + 1) % count)
        }, rotateMs)

        return () => window.clearInterval(id)
    }, [paused, reduce, count, rotateMs])

    if (count === 0) return null

    const current = quotes[index % count]

    return (
        <section
            id='thenie'
            aria-labelledby='quotes-title'
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
            onFocus={() => setPaused(true)}
            onBlur={() => setPaused(false)}
            className='relative overflow-hidden bg-brand-cream'
        >
            <FloatingBlob
                className='-left-24 top-12 h-[280px] w-[280px] text-brand-yellow-soft/35'
                amplitude={12}
                duration={11}
            />

            <FloatingBlob
                className='-right-20 bottom-20 h-[260px] w-[260px] text-brand-blob-orange/30'
                amplitude={10}
                duration={9}
                delay={1.5}
            />

            <div className='container-page section-pad relative'>
                <div className='relative mx-auto max-w-5xl'>
                    <FadeIn>
                        <div
                            className='relative bg-brand-yellow-soft/55 px-6 py-14 md:px-12 md:py-20 md:pl-[36%]'
                            style={{ borderRadius: BLOB_RADIUS }}
                        >
                            <div className='text-center'>
                                <CmsText
                                    as='h2'
                                    id='quotes-title'
                                    textKey='quotes.title'
                                    className='font-display text-4xl font-extrabold text-brand-red md:text-5xl'
                                />

                                <CmsText
                                    as='h3'
                                    textKey='quotes.subtitle'
                                    className='mt-2 font-display text-xl font-semibold text-brand-red md:text-2xl'
                                />

                                <div className='mt-8 flex justify-center'>
                                    <span className='inline-flex h-12 w-12 items-center justify-center rounded-pill bg-brand-yellow/35 text-brand-red'>
                                        <QuoteIcon aria-hidden className='h-6 w-6' />
                                    </span>
                                </div>

                                <div
                                    className='relative mx-auto mt-6 min-h-[180px] max-w-xl md:min-h-[160px]'
                                    aria-live='polite'
                                    aria-atomic='true'
                                >
                                    <AnimatePresence mode='wait' initial={false}>
                                        <motion.blockquote
                                            key={current.id}
                                            initial={reduce ? { opacity: 0 } : { opacity: 0, y: 12 }}
                                            animate={reduce ? { opacity: 1 } : { opacity: 1, y: 0 }}
                                            exit={reduce ? { opacity: 0 } : { opacity: 0, y: -12 }}
                                            transition={{ duration: reduce ? 0.15 : 0.45, ease: [0.22, 1, 0.36, 1] }}
                                            className='absolute inset-0'
                                        >
                                            <p
                                                className='text-base italic leading-relaxed text-neutral-700 md:text-lg'
                                                data-mdlfy-cms={`quotes:${current.id}:quote`}
                                            >
                                                {current.quote}
                                            </p>

                                            <DotSeparator />

                                            <footer>
                                                <p
                                                    className='font-display text-base font-bold text-brand-red'
                                                    data-mdlfy-cms={`quotes:${current.id}:author`}
                                                >
                                                    {current.author}
                                                </p>

                                                <p
                                                    className='mt-1 text-xs text-neutral-500'
                                                    data-mdlfy-cms={`quotes:${current.id}:source`}
                                                >
                                                    {current.source}
                                                </p>
                                            </footer>
                                        </motion.blockquote>
                                    </AnimatePresence>
                                </div>

                                <div className='mt-6 flex justify-center gap-2'>
                                    {quotes.map((quote, i) => (
                                        <button
                                            key={quote.id}
                                            type='button'
                                            onClick={() => setIndex(i)}
                                            aria-label={`Shko te thënia ${i + 1}`}
                                            aria-current={i === index % count || undefined}
                                            className={cn(
                                                'h-2 rounded-pill transition-all duration-300 focus:outline-none focus-visible:ring-4 focus-visible:ring-brand-yellow/60',
                                                i === index % count
                                                    ? 'w-8 bg-brand-yellow'
                                                    : 'w-2 bg-brand-red/25 hover:bg-brand-red/50'
                                            )}
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>
                    </FadeIn>

                    {/* Boy with balloons, overlapping the blob's bottom-left. Hidden on mobile. */}
                    <motion.div
                        initial={reduce ? { opacity: 0 } : { opacity: 0, x: -16 }}
                        whileInView={reduce ? { opacity: 1 } : { opacity: 1, x: 0 }}
                        viewport={{ once: true, margin: '-80px' }}
                        transition={{ duration: 0.7, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
                        aria-hidden
                        className='pointer-events-none absolute bottom-0 left-2 hidden md:block lg:left-8'
                    >
                        <Image
                            src='/images/boy-balloons.png'
                            alt=''
                            width={272}
                            height={561}
                            className='h-auto w-[260px] select-none drop-shadow-[0_24px_24px_rgba(0,0,0,0.08)] lg:w-[300px]'
                        />
                    </motion.div>
                </div>
            </div>

            <svg
                aria-hidden
                viewBox='0 0 1440 80'
                preserveAspectRatio='none'
                className='block h-10 w-full text-brand-red md:h-16'
            >
                <path
                    fill='currentColor'
                    d='M0,40 C240,80 480,0 720,30 C960,60 1200,90 1440,40 L1440,80 L0,80 Z'
                />
            </svg>
        </section>
    )
}

const DotSeparator: FC = (): ReactNode => {
    return (
        <div className='my-6 flex justify-center gap-1.5' aria-hidden>
            <span className='h-1.5 w-1.5 rounded-pill bg-brand-yellow' />
            <span className='h-1.5 w-1.5 rounded-pill bg-brand-yellow' />
            <span className='h-1.5 w-1.5 rounded-pill bg-brand-yellow' />
        </div>
    )
}

export default Quotes
